import type { UseQueryResult } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useCallback, useMemo } from "react";
import { AsyncReactSelect } from "../input";

interface ApiDataSelectProps<Record> {
	onChange: (newValue?: string) => void;
	value?: string;
	useRecordHook: (recordId?: string) => UseQueryResult<AxiosResponse<Record>>;
	getRecordValue: (record: Record) => string;
	getRecordLabel: (record: Record) => string;
	getFilteredRecords: (searchQuery: string) => Promise<AxiosResponse<Record[]>>;
	label: string;
	error?: string;
	placeholder: string;
	isClearable?: boolean;
}

const ApiDataSelect = <Record,>({
	onChange,
	value,
	useRecordHook,
	getRecordLabel,
	getRecordValue,
	getFilteredRecords,
	label,
	error,
	placeholder,
	isClearable = true,
}: ApiDataSelectProps<Record>) => {
	const { data } = useRecordHook(value);

	const formatRecord = useCallback(
		(record: Record) => {
			return {
				value: getRecordValue(record),
				label: getRecordLabel(record),
			};
		},
		[getRecordLabel, getRecordValue],
	);

	const selectValue = useMemo(() => {
		if (data?.data) {
			return formatRecord(data.data);
		}

		return null;
	}, [data, formatRecord]);

	const loadOptions = useCallback(
		async (inputValue: string) => {
			const { data } = await getFilteredRecords(inputValue);

			return data.map((record) => formatRecord(record));
		},
		[getFilteredRecords, formatRecord],
	);

	return (
		<AsyncReactSelect
			cacheOptions
			defaultOptions
			isClearable={isClearable}
			onChange={(item) => onChange(item?.value)}
			value={selectValue}
			loadOptions={loadOptions}
			label={label}
			error={error}
			placeholder={placeholder}
		/>
	);
};

export default ApiDataSelect;
